import { useMemo, useState } from 'react';
import { BellRing } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Card, CardBody, Chip, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { AccountBreadcrumbs } from './components/account-breadcrumbs';
import {
  useMyOncallAssignments,
  useMyOncallSwaps,
  useMyOncallCompensations,
  type MyOncallAssignment,
  type MyOncallSwap,
  type MyOncallCompensation,
} from '@/api/account-oncall';
import { formatDate, formatMoney } from '@/lib/format';
import { statusTone } from './components/status-tone';

const YEAR = new Date().getFullYear();
const START = `${YEAR}-01-01`;
const END = `${YEAR}-12-31`;
type View = 'assignments' | 'swaps' | 'compensations';

function LoadingSkeleton() {
  return (
    <Card flat>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} style={{ height: 24 }} />
        ))}
      </CardBody>
    </Card>
  );
}

/** On-call — the current employee's shifts, swap requests and compensations. */
export default function OncallPage() {
  const [view, setView] = useState<View>('assignments');
  const assignments = useMyOncallAssignments(START, END);
  const swaps = useMyOncallSwaps(START, END);
  const comps = useMyOncallCompensations(START, END);

  const assignmentCols = useMemo<ColumnDef<MyOncallAssignment, unknown>[]>(() => [
    { accessorKey: 'oncall_schedule_name', header: 'Schedule' },
    { accessorKey: 'oncall_date', header: 'Date', cell: ({ getValue }) => formatDate(String(getValue() ?? '')) },
    { accessorKey: 'department_name', header: 'Department', cell: ({ getValue }) => String(getValue() ?? '—') },
    { accessorKey: 'oncall_assignment_status', header: 'Status', cell: ({ getValue }) => <StatusPill tone={statusTone(String(getValue() ?? ''))}>{String(getValue() ?? '—')}</StatusPill> },
  ], []);

  const swapCols = useMemo<ColumnDef<MyOncallSwap, unknown>[]>(() => [
    { accessorKey: 'oncall_schedule_name', header: 'Schedule' },
    { accessorKey: 'oncall_date', header: 'Date', cell: ({ getValue }) => formatDate(String(getValue() ?? '')) },
    { id: 'parties', header: 'Requester → Target', cell: ({ row }) => `${row.original.requester_name} → ${row.original.target_name}` },
    { accessorKey: 'oncall_swap_status', header: 'Status', cell: ({ getValue }) => <StatusPill tone={statusTone(String(getValue() ?? ''))}>{String(getValue() ?? '—')}</StatusPill> },
  ], []);

  const compCols = useMemo<ColumnDef<MyOncallCompensation, unknown>[]>(() => [
    { accessorKey: 'oncall_schedule_name', header: 'Schedule' },
    { accessorKey: 'oncall_date', header: 'Date', cell: ({ getValue }) => formatDate(String(getValue() ?? '')) },
    { accessorKey: 'oncall_hours', header: 'Hours', cell: ({ getValue }) => `${getValue() ?? 0}h` },
    { accessorKey: 'compensation_rate', header: 'Rate', cell: ({ row }) => <Chip size="sm" tone={row.original.compensation_rate >= 2 ? 'danger' : row.original.compensation_rate > 1 ? 'warning' : 'neutral'}>{row.original.compensation_rate}x</Chip> },
    { accessorKey: 'compensation_amount', header: 'Amount', cell: ({ getValue }) => formatMoney(getValue() as number | null) },
    { accessorKey: 'oncall_compensation_status', header: 'Status', cell: ({ getValue }) => <StatusPill tone={statusTone(String(getValue() ?? ''))}>{String(getValue() ?? '—')}</StatusPill> },
  ], []);

  const active = view === 'assignments' ? assignments : view === 'swaps' ? swaps : comps;

  return (
    <Container size="full">
      <PageHeader breadcrumbs={<AccountBreadcrumbs current="On-call" />} title="On-call" description={`Your on-call activity in ${YEAR}`} />

      <Tabs value={view} onValueChange={(v) => setView(v as View)}>
        <TabsList>
          <TabsTrigger value="assignments">
            Shifts ({assignments.data?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="swaps">
            Swaps ({swaps.data?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="compensations">
            Compensations ({comps.data?.length ?? 0})
          </TabsTrigger>
        </TabsList>

        {active.isError ? (
          <Alert
            tone="danger"
            title="Failed to load on-call data"
            actions={<Button size="sm" variant="ghost" onClick={() => active.refetch()}>Retry</Button>}
          />
        ) : active.isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <TabsContent value="assignments">
              <DataTable
                columns={assignmentCols}
                data={assignments.data ?? []}
                getRowId={(r) => String(r.oncall_assignment_id)}
                emptyState={<EmptyState icon={<BellRing size={24} />} title="No on-call shifts" />}
                footer={`Showing ${assignments.data?.length ?? 0} of ${assignments.data?.length ?? 0}`}
              />
            </TabsContent>

            <TabsContent value="swaps">
              <DataTable
                columns={swapCols}
                data={swaps.data ?? []}
                getRowId={(r) => String(r.oncall_swap_id)}
                emptyState={<EmptyState icon={<BellRing size={24} />} title="No swap requests" />}
                footer={`Showing ${swaps.data?.length ?? 0} of ${swaps.data?.length ?? 0}`}
              />
            </TabsContent>

            <TabsContent value="compensations">
              <DataTable
                columns={compCols}
                data={comps.data ?? []}
                getRowId={(r) => String(r.oncall_compensation_id)}
                emptyState={<EmptyState icon={<BellRing size={24} />} title="No compensations" />}
                footer={`Showing ${comps.data?.length ?? 0} of ${comps.data?.length ?? 0}`}
              />
            </TabsContent>
          </>
        )}
      </Tabs>
    </Container>
  );
}
